"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { updateLocaleAction } from "@/app/actions";
import { useEffect, useState } from "react";
import { routing } from "@/i18n/routing";

const languages = routing.locales.map((locale) => ({
  code: locale,
  label: locale.toUpperCase(),
}));

export function LocaleToggle() {
  const t = useTranslations("LocaleToggle");
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();

  const currentLocale = mounted ? locale : routing.defaultLocale;
  const search = searchParams.toString();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);

  const handleLocaleClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    code: string,
  ) => {
    if (code === currentLocale) {
      e.preventDefault();
    }
  };

  return (
    <form
      action={updateLocaleAction}
      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-zinc-800/50 p-1 dark:border-zinc-800 dark:bg-zinc-900/50"
    >
      <input type="hidden" name="pathname" value={pathname} />
      <input type="hidden" name="search" value={search} />
      {languages.map(({ code, label }) => {
        const isSelected = mounted ? currentLocale === code : false;
        return (
          <button
            key={code}
            aria-label={t("switchTo", { locale: label })}
            aria-pressed={isSelected}
            name="locale"
            value={code}
            onClick={(e) => handleLocaleClick(e, code)}
            className={`relative flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-all cursor-pointer ${
              isSelected
                ? "bg-zinc-700 dark:bg-zinc-700 text-white"
                : "hover:bg-zinc-700/50 dark:hover:bg-zinc-700/50 text-white/50 hover:text-white/70"
            }`}
            type="submit"
            data-testid={`locale-toggle-${code}`}
          >
            {label}
            {isSelected && (
              <span className="absolute inset-0 rounded-md ring-1 ring-inset ring-white/20" />
            )}
          </button>
        );
      })}
    </form>
  );
}

export function LocaleToggleFallback() {
  return (
    <div className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-zinc-800/50 p-1 dark:border-zinc-800 dark:bg-zinc-900/50">
      {languages.map(({ code, label }) => (
        <div
          key={code}
          className="flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium text-white/50"
        >
          {label}
        </div>
      ))}
    </div>
  );
}
